// Vuetify's text inputs (v-text-field and everything built on it: textarea, select, autocomplete,
// combobox, file input) answer a focus event that leaves the focus elsewhere by focusing their input
// again. When another script moves the focus away during that event (a password manager's inline
// menu, another focus trap), the two hand the focus back and forth until the stack overflows.
// With the guard a field asks for the focus once per focus event: if the focus is gone again by
// the time the nested event arrives, the field lets the other script keep it.
export function guardRefocus(onFocus) {
  const refocusing = new WeakSet();

  return function guardedOnFocus(e) {
    if (refocusing.has(this)) {
      const input = this.$refs.input;
      if (!input || input.getRootNode().activeElement !== input) {
        return undefined;
      }
      return onFocus.call(this, e);
    }

    refocusing.add(this);
    try {
      return onFocus.call(this, e);
    } finally {
      refocusing.delete(this);
    }
  };
}

// Vue plugin: every component that inherits Vuetify's `onFocus` gets the guarded one before its
// methods are bound. Pass VTextField's `options.methods.onFocus` from every Vuetify build the app
// bundles (an app that imports both `vuetify` and `vuetify/lib` has two copies of each component).
export default function inputRefocusGuard(...vuetifyOnFocusCopies) {
  const guarded = new Map(
    vuetifyOnFocusCopies.map((onFocus) => [onFocus, guardRefocus(onFocus)]),
  );

  return {
    install(Vue) {
      Vue.mixin({
        beforeCreate() {
          const methods = this.$options.methods;
          if (methods && guarded.has(methods.onFocus)) {
            methods.onFocus = guarded.get(methods.onFocus);
          }
        },
      });
    },
  };
}
